import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AuthContext from "../../../AuthContext/authContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignInAlt,
  faRocket,
  faBroadcastTower,
  faCog,
  faBook,
  faCode,
  faShieldAlt,
  faSitemap,
  faBox,
  faCloud,
  faMobileAlt,
  faGraduationCap,
  faPuzzlePiece,
  faMobile,
  faLayerGroup,
  faCubes,
  faDesktop,
  faServer,
  faFlask,
  faLaptopCode,
  faCalendarPlus,
  faCalendarCheck,
  faUserSecret,
  faRobot,
  faFileText,
  faPhone,
  faBolt,
  faChevronDown,
  faMagic,
  faInfinity,
  faArrowRight,
  faBars,
  faTimes,
  faGlobe,
  faVideo,
  faMicrophone,
  faUsers,
  faHospital,
  faBuilding,
  faCalendarAlt,
  faHeadset,
  faNetworkWired,
  faCamera,
  faToolbox
} from "@fortawesome/free-solid-svg-icons";
import { faYoutube, faAndroid, faGithub } from "@fortawesome/free-brands-svg-icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { preloadDashboards, preloadDashboardsWhenIdle } from "../../../utils/preloadDashboards";
import "./MainNavigation.css"; // Import the revolutionary Main Navigation CSS
import "./ResponsiveLaptopFixes.css"; // Import responsive fixes for 13-inch, 15-inch laptops
import "./NavMenuRefresh.css"; // Readable dropdowns; loads last
import useReducedMotion from "../../../hooks/useReducedMotion";

const NAV_DATA = Object.freeze({
  products: {
    title: "PRODUCTS",
    icon: faRocket,
    /* Eleven entries that are not eleven of the same thing: five are the
       platform itself, three are places you build something, and the rest are
       ways of taking it all in. The `group` runs below name that split; the
       renderer emits a label whenever the run changes, so the order here IS the
       grouping and the two cannot drift apart. */
    items: [
      { name: "All Products", description: "The tools, with something to try in each", icon: faRocket, url: "/products", group: "The platform", highlight: false },
      { name: "Meetings", description: "Video rooms people join from a link", icon: faVideo, url: "/products/meetings", group: "The platform", highlight: true },
      { name: "Cloud Phone", description: "Calls in the browser, your own SIP", icon: faPhone, url: "/products/voice", group: "The platform", highlight: true },
      { name: "Live Events", description: "Webinars and broadcasts to 500K viewers", icon: faBroadcastTower, url: "/products/live", group: "The platform", highlight: true },
      { name: "AI Agents", description: "Answer your phone, book, hand over", icon: faRobot, url: "/products/agents", group: "The platform", highlight: true, isNew: true },
      { name: "Live Translation", description: "Everyone hears their own language", icon: faGlobe, url: "/products/translate", group: "The platform", highlight: true, isNew: true },
      /* The LiteDashboard ships Calls, Campaigns, Contacts, Recordings and Voice
         Clone, and NOTHING on the public site linked to any of it — /lite is
         reachable only from inside the product (the two dashboards and this
         guide), so a logged-out visitor could not find out that batch outbound
         calling exists. It points at the guide rather than at /lite itself:
         /lite/campaigns is auth-gated and bounces to /login, which is not what a
         marketing menu should hand a stranger, and the guide already carries six
         links onward into the real thing. */
      { name: "Outbound Campaigns", description: "Batch calling, run by agents or your team", icon: faBroadcastTower, url: "/campaigns", group: "The platform" },
      /* /ai-notes is a public product page and the destination of a card on the
         landing wall, and it was the only /products-shaped route with no entry
         in this menu. The recording ENGINE was reachable (DEVELOPERS > Platform
         engines > Cloud Recording) but the product a buyer would search for —
         "AI meeting notes" — was not, which is the wrong way round for a
         PRODUCTS menu. */
      { name: "AI Notes & Recording", description: "Transcripts, summaries, decisions, follow-ups", icon: faFileText, url: "/ai-notes", group: "The platform" },

      { name: "Phone Agents Studio", description: "Build and deploy phone agents", icon: faMicrophone, url: "/aiagents", group: "Studios & widgets", isNew: true },
      { name: "Widget Studio", description: "No-code embeds for your site", icon: faPuzzlePiece, url: "/widget-studio", group: "Studios & widgets" },
      { name: "AI Widgets", description: "Prebuilt widget gallery", icon: faMagic, url: "/widgets", group: "Studios & widgets" },

      { name: "Desktop & Mobile Apps", description: "Native apps for Mac, Windows, iOS, Android", icon: faDesktop, url: "/download", group: "Apps & overview" },
      { name: "Features Overview", description: "Everything in one list", icon: faBolt, url: "/features", group: "Apps & overview" }
    ]
  },
  solutions: {
    title: "SOLUTIONS",
    icon: faBuilding,
    items: [
      /* Two runs. "By situation": who you are and what you are doing, each page
         opening on a live room, a phone line or a kit to try. "Ready-made
         apps": the kits (Solutions/kitsCatalog.js), finished apps with a live
         demo, a guide and a GitHub starter; Video KYC has no guide yet, so it
         opens its demo. Products are the tools; solutions are the situations
         and the finished apps built from them. */
      { name: "All Solutions", description: "Find your situation, try it live", icon: faBuilding, url: "/solutions", group: "By situation", highlight: false },
      { name: "Remote Teams", description: "Meetings, translation, AI notes", icon: faUsers, url: "/solutions/remote-teams", group: "By situation" },
      { name: "Call Centers & Support", description: "AI answers, a person takes over", icon: faHeadset, url: "/solutions/call-centers", group: "By situation" },
      { name: "Classes & Training", description: "Polls, breakouts, recordings", icon: faGraduationCap, url: "/solutions/edtech", group: "By situation" },
      { name: "Events & Webinars", description: "A live stage and a big audience", icon: faCalendarAlt, url: "/solutions/events", group: "By situation" },
      { name: "Live Commerce", description: "Sell live and take bids", icon: faBox, url: "/solutions/live-commerce", group: "By situation" },
      { name: "Healthcare", description: "Video visits, intake, interpreting", icon: faHospital, url: "/solutions/healthcare", group: "By situation" },
      { name: "Developers", description: "One SDK for rooms, phone and agents", icon: faCode, url: "/solutions/developers", group: "By situation" },

      { name: "Telehealth Kit", description: "Waiting room, consult, signed notes", icon: faHospital, url: "/solutions/telehealth", group: "Ready-made apps", isNew: true },
      { name: "Remote Inspection Kit", description: "Claims and returns on the customer's camera", icon: faCamera, url: "/solutions/inspection", group: "Ready-made apps", isNew: true },
      { name: "Video KYC Kit", description: "Verify customers on your own servers", icon: faShieldAlt, url: "/showcases/video-kyc", group: "Ready-made apps", isNew: true },
      { name: "Multilingual Events Kit", description: "Each listener picks a language", icon: faGlobe, url: "/translation", group: "Ready-made apps" },
      { name: "Agency Phone Agents", description: "AI receptionists for your clients", icon: faPhone, url: "/agencies", group: "Ready-made apps" },
      { name: "All Kits", description: "Live demo, guide and GitHub starter", icon: faToolbox, url: "/kits", group: "Ready-made apps" },

      { name: "Case Studies", description: "What teams shipped, with numbers", icon: faLayerGroup, url: "/case-studies", group: "Before you decide" },
      { name: "Compare Platforms", description: "Head-to-head with LiveKit, Twilio, Vapi", icon: faBolt, url: "/vs", group: "Before you decide" }
    ]
  },
  developers: {
    title: "DEVELOPERS",
    icon: faCode,
    multiColumn: true,
    /* Four columns, grouped by INTENT rather than by artifact type.

       The previous three ("Docs & Start" / "AI & Voice" / "SDKs") had no
       column meaning "a runnable app". That is why QuickStart Apps, VOIP and
       Agents were filed under AI & Voice, and why ten public repositories had
       no nav presence at all: they were not forgotten, nothing could hold them.

       The long tail deliberately stays on GitHub. github.com/MediaSFU is already
       an intent-organised catalogue ("I want to... -> best starting point"), so
       this links the highest-value starters plus the catalogue itself instead of
       mirroring 27 repositories inside a dropdown.

       Each column is split into named runs by the `group` field below. The
       items are stored in group order and the renderer emits a label whenever
       the group changes, so the list stays one flat array and the labels cannot
       drift away from what they are labelling. An item without a `group` simply
       renders without one.

       COLUMN HEIGHT IS THE CAP. This renders in a fixed box with overflow:auto,
       so the tallest column decides whether the menu scrolls. Measured after the
       grouping pass: 45 items and 11 group labels come to 689px against a 700px
       box. A link row is 39px and a group label is 17px (9px for the first in a
       column, which needs no rule above it), so the remaining slack is about
       one row OR half a dozen labels -- count before adding either.

       The 700px is the flat half of `min(700px, calc(100vh - 96px))`. On any
       viewport shorter than ~796px the calc still wins and the menu scrolls,
       exactly as it did at 640px.

       The geometry lives in ResponsiveLaptopFixes.css between 1025px and 1536px,
       NOT in MainNavigation.css. Both declare the mega grid; the former wins on
       !important, so a change to the latter alone does nothing on most laptops. */
    items: [
      /* ---- Learn ----
         Three runs: what you read, what you poke at, and the guides to the
         product's own surfaces. Docs Portal and Quickstart lead because they
         are the two marked KEY. */
      { name: "Docs Portal", icon: faBook, url: "/docs/", native: true, category: "Learn", group: "Docs & API", highlight: true, isKey: true },
      { name: "Quickstart", icon: faGraduationCap, url: "/docs/usage/quickstart/", native: true, category: "Learn", group: "Docs & API", isKey: true },
      { name: "API Reference", icon: faLayerGroup, url: "/docs/api/", native: true, category: "Learn", group: "Docs & API" },
      { name: "WebRTC & SFU Guide", icon: faNetworkWired, url: "/webrtc-api", category: "Learn", group: "Docs & API", highlight: true },
      { name: "Documentation Map", icon: faSitemap, url: "/docs/guide-map/", native: true, category: "Learn", group: "Docs & API" },

      { name: "API Sandbox", icon: faFlask, url: "/sandbox", category: "Learn", group: "Try it live" },
      { name: "React Storybook", icon: faPuzzlePiece, url: "/storybook/", native: true, category: "Learn", group: "Try it live", highlight: true },
      { name: "Video Tutorials", icon: faYoutube, url: "https://www.youtube.com/playlist?list=PL1JaYsvPqLVz-ucQ8OrT4bWVkJO3kY1kD", external: true, category: "Learn", group: "Try it live" },

      { name: "Developer Console Guide", icon: faCode, url: "/documentation", category: "Learn", group: "Console & dashboard" },
      { name: "Dashboard User Guide", icon: faDesktop, url: "/user-guide", category: "Learn", group: "Console & dashboard" },
      { name: "User Guides Hub", icon: faBook, url: "/guides", category: "Learn", group: "Console & dashboard" },

      /* ---- SDKs ----
         Web and mobile were interleaved in install order, so somebody looking
         for Flutter had to read all thirteen names. Platform is the only thing
         anybody filters this column by. */
      { name: "SDKs Usage Guide", icon: faCode, url: "/docs/sdks/", native: true, category: "SDKs", group: "Start here", highlight: true, isKey: true },
      { name: "SDK Headless Guide", icon: faCode, url: "/docs/usage/headless/", native: true, category: "SDKs", group: "Start here", highlight: true },
      { name: "SDK Use Cases", icon: faRocket, url: "/use-cases", category: "SDKs", group: "Start here" },

      { name: "React SDK", icon: faCode, url: "/docs/sdks/reactjs/", native: true, category: "SDKs", group: "Web" },
      { name: "Angular SDK", icon: faCode, url: "/docs/sdks/angular/", native: true, category: "SDKs", group: "Web" },
      { name: "Vue.js SDK", icon: faCode, url: "/docs/sdks/vue/", native: true, category: "SDKs", group: "Web" },
      { name: "Shared TS Core", icon: faCode, url: "/docs/sdks/shared/", native: true, category: "SDKs", group: "Web" },

      { name: "React Native SDK", icon: faMobileAlt, url: "/docs/sdks/react-native/", native: true, category: "SDKs", group: "Mobile & native" },
      { name: "React Native Expo", icon: faMobileAlt, url: "/docs/sdks/react-native-expo/", native: true, category: "SDKs", group: "Mobile & native" },
      { name: "Flutter SDK", icon: faMobileAlt, url: "/docs/sdks/flutter/", native: true, category: "SDKs", group: "Mobile & native" },
      { name: "Kotlin/Android SDK", icon: faAndroid, url: "/docs/sdks/kotlin/", native: true, category: "SDKs", group: "Mobile & native" },
      { name: "Swift / iOS SDK", icon: faMobileAlt, url: "/docs/sdks/swift/", native: true, category: "SDKs", group: "Mobile & native" },
      { name: "Unity SDK", icon: faCubes, url: "/docs/sdks/unity/", native: true, category: "SDKs", group: "Mobile & native" },

      /* ---- Run a working app ----
         Nine repositories that are not the same kind of thing: four are a
         starting point you build on top of, five are finished applications you
         run and read. */
      { name: "QuickStart Apps", icon: faRocket, url: "https://github.com/MediaSFU/MediaSFU-QuickStart-Apps", external: true, category: "Run a working app", group: "Start from a template", highlight: true, isKey: true },
      { name: "SpacesTek Starters", icon: faCubes, url: "https://github.com/MediaSFU/SpacesTekFinal", external: true, category: "Run a working app", group: "Start from a template" },
      { name: "AI Agent Starters", icon: faRobot, url: "https://github.com/MediaSFU/Agents", external: true, category: "Run a working app", group: "Start from a template" },
      { name: "All Starters on GitHub", icon: faGithub, url: "https://github.com/MediaSFU", external: true, category: "Run a working app", group: "Start from a template" },

      { name: "Familiar Calls", icon: faPhone, url: "https://github.com/MediaSFU/mediasfu-familiar-calls", external: true, category: "Run a working app", group: "Complete apps" },
      { name: "Live Auction", icon: faBroadcastTower, url: "https://github.com/MediaSFU/mediasfu-live-auction", external: true, category: "Run a working app", group: "Complete apps" },
      { name: "Watch Together", icon: faUsers, url: "https://github.com/MediaSFU/mediasfu-watch-together", external: true, category: "Run a working app", group: "Complete apps" },
      { name: "VOIP Reference App", icon: faHeadset, url: "https://github.com/MediaSFU/VOIP", external: true, category: "Run a working app", group: "Complete apps" },
      { name: "MediaSFU Open (self-host)", icon: faServer, url: "https://github.com/MediaSFU/MediaSFUOpen", external: true, category: "Run a working app", group: "Complete apps", highlight: true },

      /* ---- Extend & operate ----
         The twelve here answered three different questions in one list: what
         can I build with no code, what do I drop into an app I already have,
         and what does the platform run for me. */
      { name: "Widget Studio", icon: faPuzzlePiece, url: "/widget-studio", category: "Extend & operate", group: "No-code studios", highlight: true },
      { name: "Vibe Studio", icon: faMagic, url: "/vibe-studio", category: "Extend & operate", group: "No-code studios", isNew: true },
      // { name: "Widget Lab", icon: faFlask, url: "/widget-lab", category: "Extend & operate" },
      { name: "Widgets Gallery", icon: faMagic, url: "/widgets", category: "Extend & operate", group: "No-code studios" },

      { name: "Widgets Package", icon: faGithub, url: "https://github.com/MediaSFU/mediasfu-widgets", external: true, category: "Extend & operate", group: "Embed in your app" },
      { name: "Iframe Embedding", icon: faCode, url: "/prebuilt", category: "Extend & operate", group: "Embed in your app" },
      { name: "MCP SDK Tools", icon: faGithub, url: "https://github.com/MediaSFU/mcp-sdk-tools", external: true, category: "Extend & operate", group: "Embed in your app", isNew: true },

      { name: "AI Agents Guide", icon: faRobot, url: "/agents", category: "Extend & operate", group: "Platform engines" },
      { name: "SIP/Telephony Engine", icon: faPhone, url: "/telephony", category: "Extend & operate", group: "Platform engines" },
      { name: "Cloud Phone / VOIP Apps", icon: faHeadset, url: "https://github.com/MediaSFU/VOIP", external: true, category: "Extend & operate", group: "Platform engines" },
      { name: "Translation Config", icon: faGlobe, url: "/translation", category: "Extend & operate", group: "Platform engines" },
      { name: "Cloud Recording", icon: faVideo, url: "/cloud-recording", category: "Extend & operate", group: "Platform engines" }
    ]
  },
  /* DEMOS, LIVE ROOMS and KITS answer the same visitor question, "can I try
     it first?", so they share one entry instead of three: showcases were filed
     under Developers (they are for everyone) and kits under Solutions. Clicking
     the title still opens /demos, the first item. */
  demos: {
    title: "DEMOS",
    icon: faHeadset,
    items: [
      { name: "Feature demos", description: "One feature at a time: an AI agent, the phone line, translation", icon: faHeadset, url: "/demos" },
      { name: "Use-case rooms", description: "An auction, a classroom, a broadcast: open one, join from your phone", icon: faBroadcastTower, url: "/showcases" },
      { name: "Kits & starters", description: "Full apps to clone, with a guide and a GitHub starter", icon: faToolbox, url: "/kits", isNew: true }
    ]
  },
  events: {
    title: "MEETINGS",
    icon: faCalendarPlus,
    special: true
  }
});

const RevolutionaryNavigation = () => {
  const auth = useContext(AuthContext);
  const history = useHistory();
  const { reduceMotion } = useReducedMotion();
  const shouldAnimateNav = !reduceMotion;
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState("");
  const [eventsMessage, setEventsMessage] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef(null);
  const navData = NAV_DATA;
  const location = useLocation();

  /* Which nav entry is the page you are on.
   *
   * The bar had no current-route state at all: on /demos, DEMOS computed to the
   * same rgb(226, 232, 240) as PRICING and as every dropdown title, so the only
   * thing that ever marked an entry was the browser's own focus ring — which is
   * not a "you are here", it is a "you last clicked here", and it disappears the
   * moment focus moves.
   *
   * Every dropdown item already carries the `url` it navigates to, so the map
   * is derived rather than maintained by hand: a second copy of these paths
   * would drift the first time an item moves between menus. `native: true`
   * items point outside the SPA and are skipped.
   *
   * Longest match wins, so /products/agents marks PRODUCTS through its own
   * entry rather than through the shorter /products one, and a nested route
   * still lights its parent. */
  const currentNavKey = useMemo(() => {
    const path = (location?.pathname || '/').replace(/\/+$/, '') || '/';
    let best = null;
    let bestLength = 0;

    const consider = (key, url) => {
      if (!url || url === '/' || !url.startsWith('/')) return;
      const clean = url.replace(/\/+$/, '');
      if (path !== clean && !path.startsWith(`${clean}/`)) return;
      if (clean.length <= bestLength) return;
      best = key;
      bestLength = clean.length;
    };

    /* The two entries that are plain links rather than dropdowns. */
    consider('pricing', '/pricing');

    Object.entries(NAV_DATA).forEach(([key, data]) => {
      (data.items || []).forEach((item) => {
        if (item.native) return;
        consider(key, item.url);
      });
    });

    return best;
  }, [location]);

  const closeMobileNav = useCallback(() => {
    setIsOpen(false);
    setActiveDropdown("");
  }, []);

  // Signed-in visitors will most likely open the dashboard next; fetch its
  // code while the browser is idle so /dashboard opens without a loader.
  useEffect(() => {
    if (auth.token) preloadDashboardsWhenIdle();
  }, [auth.token]);
  
  // Enhanced mouse interaction refs for smooth dropdown behavior
  const hoverTimerRef = useRef(null);  // Timer for delayed dropdown opening
  const closeTimerRef = useRef(null);  // Timer for delayed dropdown closing  
  const activeDropdownRef = useRef(null); // Reference to currently active dropdown

  // Mouse tracking for neural effects and dropdown management
  useEffect(() => {
    if (!shouldAnimateNav) {
      setMousePosition({ x: 50, y: 50 });
    }
  }, [shouldAnimateNav]);

  useEffect(() => {
    let lastMoveTime = 0;
    const handleMouseMove = (e) => {
      // Throttle to ~4 calls/sec
      const now = Date.now();
      if (now - lastMoveTime < 250) return;
      lastMoveTime = now;

      if (!shouldAnimateNav || !navRef.current) {
        return;
      }

      const rect = navRef.current.getBoundingClientRect();
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100
      });

      // Check if mouse is outside navigation area and dropdown area, then close dropdowns
      if (!isMobileDevice() && activeDropdown) {
        // Check if mouse is within the navigation bar area
        const isWithinNav = e.clientY >= rect.top - 20 && 
                           e.clientY <= rect.bottom + 20 && 
                           e.clientX >= rect.left - 20 && 
                           e.clientX <= rect.right + 20;

        // Check if mouse is within any dropdown area
        let isWithinDropdown = false;
        const dropdownElements = document.querySelectorAll('[data-dropdown-active="true"], .nav-dropdown, .nav-events-dropdown');
        
        for (let dropdown of dropdownElements) {
          const dropdownRect = dropdown.getBoundingClientRect();
          if (e.clientX >= dropdownRect.left - 10 && 
              e.clientX <= dropdownRect.right + 10 && 
              e.clientY >= dropdownRect.top - 10 && 
              e.clientY <= dropdownRect.bottom + 10) {
            isWithinDropdown = true;
            break;
          }
        }

        // Only close if mouse is outside both nav and dropdown areas
        if (!isWithinNav && !isWithinDropdown) {
          if (!closeTimerRef.current) {
            closeTimerRef.current = setTimeout(() => {
              setActiveDropdown("");
              activeDropdownRef.current = null;
            }, 300); // 300ms delay before closing when outside all areas
          }
        } else {
          // Mouse is within nav or dropdown area, cancel any pending close
          if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
          }
        }
      }
    };

    let lastScrollTime = 0;
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime < 100) return;
      lastScrollTime = now;
      setIsScrolled(window.scrollY > 50);
      
      // Close dropdowns on scroll
      if (activeDropdown && !isMobileDevice()) {
        setActiveDropdown("");
        // Clear timers
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      }
    };

    const handleResize = () => {
      // Close any open dropdowns when switching between mobile/desktop
      if (isMobileDevice() && activeDropdown) {
        // Keep dropdown open on mobile unless explicitly closed
      } else if (!isMobileDevice()) {
        // On desktop, close dropdown to reset state
        setActiveDropdown("");
        // Clear timers
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      }
    };

    if (shouldAnimateNav) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (shouldAnimateNav) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      // Cleanup timers on unmount
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [activeDropdown, shouldAnimateNav]);

  // Subscription and balance checking logic
  const checkSubscription = (authContext) => {
      const overViewData = authContext?.summaryData;
  return overViewData.userSubscriptionValidity &&
    parseInt(overViewData.userSubscriptionValidity) > new Date().getTime()
    ? true
    : false;
  };

  const checkBalance = async (balanceData, environment = "sandbox") => {
    if (!balanceData) return false;
    let available = 0;
    switch (environment) {
      case "production":
        available = balanceData?.meetingMinutesActual?.available || 0;
        break;
      case "sandbox":
        available = balanceData?.meetingMinutesSandbox?.available || 0;
        break;
      case "demo":
        available = balanceData?.meetingMinutesDemo?.available || 0;
        break;
      default:
        return false;
    }
    return available > 0.5;
  };

  // Event handling
   const handleCreateEvent = async () => {
    try {
      // Must be logged in
      if (!auth.token) {
        // Show a small "Please log in" message with an option to do a Demo environment
        setEventsMessage({
          type: "not-logged-in",
          text: "Sign in to create a saved event, or continue with a no-login demo.",
        });
        return;
      }
      const userRole = auth?.role || "guest";

      // Attempt Production
      const subscriptionValid = checkSubscription(auth);
      const hasProdBalance = await checkBalance(
        auth?.summaryData?.userBalance,
        "production"
      );

      if (subscriptionValid && hasProdBalance) {
        createRoom("production");
      } else {
        // Production not available
        if (userRole === "child" || userRole === "parent") {
          // No sandbox fallback
          setEventsMessage({
            type: "error",
            text: "Production isn't available, and your role can't create sandbox. Please top up or contact support.",
          });
        } else {
          // Try sandbox
          const hasSandboxBalance = await checkBalance(
            auth?.summaryData?.userBalance,
            "sandbox"
          );
          if (!hasSandboxBalance) {
            setEventsMessage({
              type: "error",
              text: "Insufficient sandbox minutes. Please top up or contact support.",
            });
          } else {
            createRoom("sandbox");
          }
        }
      }
    } catch (error) {
      console.error("Error creating event:", error);
      setEventsMessage({
        type: "error",
        text: "An error occurred. Please try again or contact support.",
      });
    }
  };

  const handleJoinEvent = () => {
    switchToMeeting();
  };

  const switchToMeeting = () => {
    closeMobileNav();
    Cookies.set("activeLink", "meeting", { expires: 7 });
    history.push("/meeting");
  };

  const createRoom = (environment) => {
    try {
      // environment: 'production' or 'sandbox'
      if (environment === "demo") {
        // Do something special for demo
        switchToMeeting();
        return;
      }
      const act_URL = "meeting";
      const roomType = "conference";

      let activeKey = auth?.summaryData?.userAPIKeys.find(
        (k) => k.type === environment
      );

      const meetingRoomData = {
        url: `https://${act_URL}.mediasfu.com/${act_URL}/start`,
        apiKey: activeKey?.apiKey || null,
        apiUserName: activeKey?.user?.username || null,
        username: auth?.username,
        eventType: roomType,
        environment: environment,
      };

      auth.updateMeetingRoomData(meetingRoomData);
      
      // Store API credentials in cookies for meeting interface
      if (activeKey?.apiKey) {
        Cookies.set('apiKey', activeKey.apiKey, { expires: 7 });
      }
      if (activeKey?.user?.username) {
        Cookies.set('apiUserName', activeKey.user.username, { expires: 7 });
      }
      
      switchToMeeting();
    } catch (error) {
      console.error("Error creating room:", error);
      setEventsMessage({
        type: "error",
        text: "An error occurred. Please try again or contact support.",
      });
    }
  };

  const handleSignOut = (e) => {
    e.preventDefault();
    closeMobileNav();
    auth.logout();
    Cookies.set("activeLink", "login", { expires: 7 });
    history.push("/login");
  };

  // If they choose "Log In" from not-logged-in message
  const handleLoginClick = () => {
    setEventsMessage(null);
    closeMobileNav();
    Cookies.set("activeLink", "login", { expires: 7 });
    history.push("/login");
  };

  // If they choose "Demo" from not-logged-in message
  const handleDemoClick = () => {
    // We'll create a 'demo' environment room or do something similar
    // Or just navigate them to a "demo" route
    setEventsMessage(null);
    closeMobileNav();
    createRoom("demo");
  };

  // Close dropdown when clicking outside (for mobile and desktop)
  const handleOutsideClick = useCallback((e) => {
    // Don't interfere with select dropdowns
    if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') {
      return;
    }
    
    // Don't interfere with elements inside select dropdowns
    if (e.target.closest('select')) {
      return;
    }
    
    if (navRef.current && !navRef.current.contains(e.target)) {
      setActiveDropdown("");
      // Clear any pending timers
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    }
  }, []);

  // Add click outside listener for both mobile and desktop
  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  // Helper function to detect mobile/touch devices
  // Only use screen width — touch-capable laptops should behave as desktop
  const isMobileDevice = () => {
    return window.innerWidth <= 1024;
  };

  useEffect(() => {
    if (!isOpen || !activeDropdown || !isMobileDevice() || !navRef.current) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      const navLinks = navRef.current?.querySelector('.nav-links');
      const activeItem = navRef.current?.querySelector('.nav-item.active');
      if (!navLinks || !activeItem) return;

      const navLinksRect = navLinks.getBoundingClientRect();
      const activeItemRect = activeItem.getBoundingClientRect();

      navLinks.scrollBy({
        top: activeItemRect.top - navLinksRect.top - 12,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [activeDropdown, isOpen, reduceMotion]);

  const toggleDropdown = (dropdown) => {
    // Clear any pending timers
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    
    setActiveDropdown(activeDropdown === dropdown ? "" : dropdown);
    setEventsMessage(null);
  };

  const handleTopNavClick = (e, key, data) => {
    e.preventDefault();

    // Keep mobile behavior: tap opens/closes dropdown sections.
    if (isMobileDevice()) {
      toggleDropdown(key);
      return;
    }

    // Special nav sections without a direct page keep dropdown behavior.
    if (data.special || !data.items || data.items.length === 0) {
      toggleDropdown(key);
      return;
    }

    const primaryItem = data.items.find((item) => item.url);
    if (!primaryItem) {
      toggleDropdown(key);
      return;
    }

    setActiveDropdown("");
    setEventsMessage(null);

    if (primaryItem.external) {
      window.open(primaryItem.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (primaryItem.native) {
      window.open(primaryItem.url, "_blank", "noopener,noreferrer");
      return;
    }

    history.push(primaryItem.url);
  };

  const handleMouseEnter = (dropdown) => {
    if (!shouldAnimateNav) {
      return;
    }
    // Clear any pending close timers
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // Only handle mouse enter on desktop devices
    if (!isMobileDevice()) {
      // Check if this dropdown has submenu items
      const hasSubItems = navData[dropdown] &&
        ((navData[dropdown].items && navData[dropdown].items.length > 0) ||
          navData[dropdown].special);
      
      if (hasSubItems) {
        // Clear any existing hover timer
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
        
        // Set a delay for opening the dropdown to avoid accidental opens
        hoverTimerRef.current = setTimeout(() => {
          setActiveDropdown(dropdown);
          setEventsMessage(null);
          activeDropdownRef.current = dropdown;
        }, 300); // 300ms delay before opening
      } else {
        // For items without submenus, don't set dropdown
        setActiveDropdown("");
        activeDropdownRef.current = null;
      }
    }
  };

  const handleMouseLeave = (dropdown = null) => {
    if (!shouldAnimateNav) {
      return;
    }
    // Clear any pending hover timers
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    // Only close on mouse leave for desktop devices
    if (!isMobileDevice()) {
      // Set a longer delay before closing to allow mouse movement to dropdown
      closeTimerRef.current = setTimeout(() => {
        setActiveDropdown("");
        activeDropdownRef.current = null;
      }, 400); // Increased to 400ms delay before closing
    }
  };

  const handleDropdownMouseEnter = () => {
    if (!shouldAnimateNav) {
      return;
    }
    // When mouse enters dropdown area, cancel any pending close timers
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    if (!shouldAnimateNav) {
      return;
    }
    // When mouse leaves dropdown area, close it with a shorter delay
    if (!isMobileDevice()) {
      closeTimerRef.current = setTimeout(() => {
        setActiveDropdown("");
        activeDropdownRef.current = null;
      }, 200); // Shorter delay when leaving dropdown
    }
  };

  /* One list, rendered with its group labels.
   *
   * A label is emitted whenever the run changes group, which is why the items
   * are stored in group order: the arrays stay flat, nothing else has to know
   * about grouping, and a label cannot drift away from what it labels the way a
   * parallel table of headings would. An item with no `group` renders without
   * one, so a menu that has not been grouped is unaffected.
   *
   * Shared by the mega menu's four columns and by the single-column dropdowns,
   * so the two can never disagree about what a group boundary looks like. */
  const renderGroupedItems = (items) =>
    items.map((item, index, run) => (
      <React.Fragment key={index}>
        {item.group && item.group !== run[index - 1]?.group && (
          <div
            className={`dropdown-group-label${index === 0 ? ' is-first' : ''}`}
            aria-hidden="true"
          >
            {item.group}
          </div>
        )}
        <DropdownItem item={item} />
      </React.Fragment>
    ));

  const renderDropdownContent = (key, data) => {
    if (key === 'events') {
      return (
        <div className="nav-events-dropdown">
          <div className="events-options">
            <button type="button" className="event-option create-event" onClick={handleCreateEvent}>
              <div className="event-option-icon">
                <FontAwesomeIcon icon={faCalendarPlus} />
              </div>
              <div className="event-option-content">
                <h4>Start a meeting</h4>
                <p>A room in seconds, with AI notes and live translation</p>
              </div>
              <div className="event-option-arrow">
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
            </button>

            <button type="button" className="event-option join-event" onClick={handleJoinEvent}>
              <div className="event-option-icon">
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
              <div className="event-option-content">
                <h4>Join a meeting</h4>
                <p>Enter a room name or open your invite link</p>
              </div>
              <div className="event-option-arrow">
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
            </button>
          </div>

          {/* The two things people look for next to a meeting button. */}
          <div className="events-shortcuts">
            <Link to="/meeting/notes" className="events-shortcut" onClick={closeMobileNav}>
              <FontAwesomeIcon icon={faFileText} />
              <span><strong>AI meeting notes</strong> Summaries of past meetings</span>
            </Link>
            <Link to="/download" className="events-shortcut" onClick={closeMobileNav}>
              <FontAwesomeIcon icon={faMobileAlt} />
              <span><strong>Get the apps</strong> iPhone, Android, desktop</span>
            </Link>
          </div>

          {eventsMessage && (
            <div className={`events-message ${eventsMessage.type}`}>
              <div className="message-content">
                <p>{eventsMessage.text}</p>
                {eventsMessage.type === "not-logged-in" && (
                  <div className="message-actions">
                    <button className="btn-neural btn-primary" onClick={handleLoginClick}>
                      <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
                      Sign in
                    </button>
                    <button className="btn-neural btn-secondary" onClick={handleDemoClick}>
                      <FontAwesomeIcon icon={faRocket} className="me-2" />
                      Continue with demo
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Multi-column layout for developers menu
    if (data.multiColumn) {
      const categories = [...new Set(data.items.map(item => item.category).filter(Boolean))];
      
      return (
        <div
          className="nav-dropdown multi-column"
          role="menu"
          aria-label={`${data.title} menu`}
        >
          <div className="dropdown-overview">
            <div className="dropdown-overview__copy">
              <span className="dropdown-overview__eyebrow">Developer center</span>
              <strong>Start with the surface that matches your build.</strong>
            </div>
            <Link
              to="/quick-usage"
              className="dropdown-overview__link"
              onClick={closeMobileNav}
            >
              Compare SDK paths
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
          <div className="dropdown-columns">
            {categories.map(category => (
              <div
                key={category}
                /* A slug rather than :nth-child, so a column keeps its identity
                   colour if the order ever changes and loses it visibly — as a
                   missing class — rather than silently wearing its neighbour's. */
                className={`dropdown-column dropdown-column--${category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="category-header">
                  <FontAwesomeIcon icon={getCategoryIcon(category)} />
                  <span>{category}</span>
                </div>
                <div className="category-items-vertical">
                  {renderGroupedItems(data.items.filter(item => item.category === category))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Regular dropdown content
    const categories = [...new Set(data.items.map(item => item.category).filter(Boolean))];

    return (
      <div className="nav-dropdown" role="menu" aria-label={`${data.title} menu`}>
        {categories.length > 0 ? (
          categories.map(category => (
            <div key={category} className="dropdown-category">
              <div className="category-header">
                <FontAwesomeIcon icon={getCategoryIcon(category)} />
                <span>{category}</span>
              </div>
              <div className="category-items">
                {renderGroupedItems(data.items.filter(item => item.category === category))}
              </div>
            </div>
          ))
        ) : (
          <div className="dropdown-items">
            {renderGroupedItems(data.items)}
          </div>
        )}
      </div>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Learn': faBook,
      'SDKs': faCode,
      'Run a working app': faRocket,
      'Extend & operate': faPuzzlePiece,
      /* Old names kept: other menus still use them, and an unknown category
         silently falls back to faPuzzlePiece rather than failing loudly. */
      'Docs & Start': faBook,
      'AI & Voice': faRobot,
      'Tools': faCog
    };
    return icons[category] || faPuzzlePiece;
  };

  const isInternalRoute = (url = "") => /^\/(?!\/)/.test(url);

  const DropdownItem = ({ item }) => {
    const className = `dropdown-item ${item.highlight ? 'highlight' : ''} ${item.type ? `type-${item.type}` : ''}`;
    const content = (
      <>
        <div className="item-icon">
          <FontAwesomeIcon icon={item.icon} />
        </div>
        <div className="item-content">
          <span className="item-name">{item.name}</span>
          {item.description && <span className="item-description">{item.description}</span>}
        </div>
        {(item.external || item.isNew || item.isKey) && (
          <div className="item-meta">
            {item.external && <FontAwesomeIcon icon={faArrowRight} className="external-icon" />}
            {item.isNew && <div className="highlight-badge">NEW</div>}
            {item.isKey && <div className="key-badge">KEY</div>}
          </div>
        )}
      </>
    );

    if (!item.external && !item.native && isInternalRoute(item.url)) {
      return (
        <Link
          to={item.url}
          className={className}
          role="menuitem"
          onClick={() => {
            closeMobileNav();
          }}
        >
          {content}
        </Link>
      );
    }

    return (
      <a
        href={item.url}
        target={item.external || item.native ? "_blank" : "_self"}
        rel={item.external || item.native ? "noopener noreferrer" : ""}
        className={className}
        role="menuitem"
        onClick={closeMobileNav}
      >
        {content}
      </a>
    );
  };

  /* MEETINGS is an action, not a category — it is pulled out of the browse
     group and rendered beside DEMOS below. */
  const navEntries = Object.entries(navData);
  const browseNavEntries = navEntries.filter(([key]) => key !== 'events' && key !== 'demos');
  const demosNavEntry = navEntries.find(([key]) => key === 'demos');
  const eventsNavEntry = navEntries.find(([key]) => key === 'events');

  /* One renderer for both nav groups so the browse dropdowns and the MEETINGS
     launcher stay identical in behaviour while sitting in different groups. */
  const renderNavItem = (key, data, isAct = false) => (
    <div
      key={key}
      className={`nav-item ${activeDropdown === key ? 'active' : ''}`}
      onMouseEnter={() => handleMouseEnter(key)}
      onMouseLeave={() => handleMouseLeave(key)}
    >
      <div
        className={`nav-link${isAct ? ' nav-link--act' : ''}${currentNavKey === key ? ' nav-link--current' : ''}`}
        aria-current={currentNavKey === key ? 'page' : undefined}
        onClick={(e) => handleTopNavClick(e, key, data)}
      >
        {isAct && <i className="nav-live-dot" aria-hidden="true" />}
        <span className="link-text">{data.title}</span>
        {!data.special && <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />}
        <div className="link-neural-glow"></div>
      </div>

      {activeDropdown === key && !data.special && (
        isMobileDevice() ? (
          renderDropdownContent(key, data)
        ) : (
          <div
            className={`dropdown-wrapper ${
              data.multiColumn
                ? 'dropdown-wrapper--mega'
                : `dropdown-wrapper--directory ${data.items?.length > 5 ? 'dropdown-wrapper--wide' : ''}`
            }`}
            data-dropdown-active="true"
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              paddingTop: '5px',
              zIndex: 2000
            }}
          >
            {renderDropdownContent(key, data)}
          </div>
        )
      )}
      {activeDropdown === key && data.special && (
        isMobileDevice() ? (
          renderDropdownContent(key, data)
        ) : (
          <div
            className="dropdown-wrapper"
            data-dropdown-active="true"
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              paddingTop: '5px',
              zIndex: 2000
            }}
          >
            {renderDropdownContent(key, data)}
          </div>
        )
      )}
    </div>
  );

  return (
    <nav 
      ref={navRef}
      className={`revolutionary-nav ${isScrolled ? 'scrolled' : ''} ${reduceMotion ? 'reduce-motion' : ''}`}
      style={shouldAnimateNav ? {
        '--mouse-x': `${mousePosition.x}%`,
        '--mouse-y': `${mousePosition.y}%`
      } : undefined}
    >
      {/* MediaSFU network background */}
      {shouldAnimateNav && (
        <>
          <div className="nav-neural-bg"></div>
          <div className="nav-particles"></div>
        </>
      )}
      
      <div className="nav-container">
        {/* Revolutionary Brand */}
        <div className="nav-brand">
          <Link to="/" className="brand-link">
            <div className="brand-logo">
              <img src="/images/logo60.png" alt="MediaSFU" width="50" height="50" />
              <div className="logo-glow"></div>
            </div>
            <div className="brand-neural-text">
              <span className="brand-name">MediaSFU</span>
            </div>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          type="button"
          className={`nav-mobile-toggle ${isOpen ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
        >
          <div className="toggle-icon">
            <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
          </div>
        </button>

        {/* Navigation Menu */}
        <div className={`nav-menu ${isOpen ? 'open' : ''}`}>
          <div className="nav-links">
            {/* Browse group: the three "what is this / who is it for / how do I
                build" categories. MEETINGS used to sit here as a fourth entry,
                which read as another category when it actually launches a room —
                it now sits with DEMOS in the act group below. */}
            {browseNavEntries.map(([key, data]) => renderNavItem(key, data))}

            <span className="nav-group-divider" aria-hidden="true" />

            {/* Act group: the two entries that start something rather than
                describe it. Both carry a live dot so they don't read as more
                pages to browse. DEMOS first — it is the lower-commitment of the
                two, and MEETINGS drops you straight into a room. */}
            {demosNavEntry && renderNavItem(demosNavEntry[0], demosNavEntry[1], true)}

            {eventsNavEntry && renderNavItem(eventsNavEntry[0], eventsNavEntry[1], true)}

            <span className="nav-group-divider" aria-hidden="true" />

            {/* Pricing stays last before auth: left-to-right scanning maps to the
                decision funnel, and every comparable platform trains that
                expectation. Leading with price would also make cost the brand's
                first claim, which the hero already states with its evidence. */}
            <div className="nav-item">
              <Link
                to="/pricing"
                className={`nav-link static${currentNavKey === 'pricing' ? ' nav-link--current' : ''}`}
                aria-current={currentNavKey === 'pricing' ? 'page' : undefined}
                onClick={closeMobileNav}
              >
                <span className="link-text">PRICING</span>
                <div className="link-neural-glow"></div>
              </Link>
            </div>

            {/* <div className="nav-item">
              <a href="/forums" className="nav-link static">
                <span className="link-text">FORUMS</span>
                <div className="link-neural-glow"></div>
              </a>
            </div> */}

            {/* <div className="nav-item">
              <a href="/support" className="nav-link static">
                <span className="link-text">SUPPORT</span>
                <div className="link-neural-glow"></div>
              </a>
            </div> */}
          </div>

        </div>

        {/* Auth lives in the BAR, not in the collapsible menu.
            It used to be the last child of .nav-menu, and at 1024px and below
            .nav-menu goes off-canvas (transform: translateX(-100%), visibility:
            hidden) — which took LOGIN with it. On a MacBook with a window that
            is not maximised that lands around 1000px, so the single most
            important control on the page vanished into a hamburger while the
            rest of the layout still looked like a desktop. As a sibling of the
            menu it stays in the bar at every width; the media query below puts
            it beside the toggle rather than under it. */}
        <div className="nav-auth">
          {auth.token ? (
            <div className="auth-user">
              <Link
                to="/dashboard"
                className="btn-neural btn-dashboard"
                onClick={closeMobileNav}
                onMouseEnter={preloadDashboards}
                onFocus={preloadDashboards}
                onTouchStart={preloadDashboards}
              >
                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                <span>DASHBOARD</span>
              </Link>
              <button onClick={handleSignOut} className="btn-neural btn-signout">
                <span>SIGN OUT</span>
              </button>
            </div>
          ) : (
            <div className="auth-guest">
              <Link
                to="/login"
                className="btn-neural btn-login"
                onClick={() => {
                  closeMobileNav();
                  Cookies.set("activeLink", "login", { expires: 7 });
                }}
              >
                <FontAwesomeIcon icon={faSignInAlt} className="btn-login-icon" />
                <span>LOGIN</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default RevolutionaryNavigation;
